import type { BookContent, GarmentContent, GarmentSection } from '../../types/records'

const SECTION_LABELS: Record<GarmentSection, string> = {
  donna: 'Donna',
  uomo: 'Uomo',
  bambino: 'Bambino',
}

const CATEGORY_LABELS: Record<string, string> = {
  bodice: 'Corpetto',
  shirt: 'Camicia',
  blouse: 'Camicetta',
  dress: 'Abito',
  skirt: 'Gonna',
  trousers: 'Pantalone',
  jacket: 'Giacca',
  coat: 'Cappotto',
  vest: 'Gilet',
  sleeve: 'Manica',
  collar: 'Colletto',
  other: 'Altro',
}

interface Props {
  garmentId: string
  garment: GarmentContent
  book: BookContent | null
  onBack: () => void
  onCreateProject?: (garmentId: string) => void
}

export function GarmentDetail({ garmentId, garment, book, onBack, onCreateProject }: Props) {
  const measurementMap = new Map(
    (book?.measurementsBase ?? []).map(m => [m.code, m.italianName]),
  )

  return (
    <section className="tab-section detail-section">
      <button className="back-button" onClick={onBack}>← Torna alla lista</button>

      <header className={`detail-header card section-${garment.section}`}>
        <div className="header-badges">
          <span className={`badge section-${garment.section}`}>
            {SECTION_LABELS[garment.section]}
          </span>
          <span className="badge category">
            {CATEGORY_LABELS[garment.category] ?? garment.category}
          </span>
          {garment.scale && <span className="badge scale">scala {garment.scale}</span>}
          {garment.easeAllowanceDegree && (
            <span className="badge ease">vestibilità grado {garment.easeAllowanceDegree}</span>
          )}
        </div>
        <h3>{garment.name}</h3>
        <p className="header-meta">
          pp. {garment.startPage}{garment.startPage !== garment.endPage ? `-${garment.endPage}` : ''} del libro
        </p>
      </header>

      <div className="detail-block">
        <h4>
          Misure richieste <span className="count">({garment.measurementsRequired.length})</span>
        </h4>
        {garment.measurementsRequired.length === 0 ? (
          <p className="muted">Nessuna.</p>
        ) : (
          <ul className="codes-list">
            {garment.measurementsRequired.map(code => (
              <li key={code}>
                <code>{code}</code>
                {measurementMap.has(code) && (
                  <span className="muted">{measurementMap.get(code)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="detail-block">
        <h4>
          Formule riusate <span className="count">({garment.formulas.length})</span>
        </h4>
        {garment.formulas.length === 0 ? (
          <p className="muted">Nessuna.</p>
        ) : (
          <ul className="formulas-list">
            {garment.formulas.map((f, i) => (
              <li key={i}>
                <span className="formula-name">{f.name}</span>
                {' = '}
                <code>{f.formula}</code>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="detail-block">
        <h4>
          Pezzi del cartamodello <span className="count">({garment.pieces.length})</span>
        </h4>
        {garment.pieces.map(p => (
          <details key={p.id} className="piece-block" open={garment.pieces.length === 1}>
            <summary>
              <strong>{p.italianLabel}</strong>
              <span className="muted"> — {p.points.length} punti, {p.segments.length} segmenti</span>
            </summary>
            <div className="piece-content">
              <h5>Punti</h5>
              <table className="points-table">
                <thead>
                  <tr>
                    <th>Punto</th>
                    <th>Posizione</th>
                    <th>X</th>
                    <th>Y</th>
                  </tr>
                </thead>
                <tbody>
                  {p.points.map((pt, i) => (
                    <tr key={i}>
                      <td><code>{pt.label}</code></td>
                      <td>{pt.positionDescription}</td>
                      <td><code>{pt.formulaX}</code></td>
                      <td><code>{pt.formulaY}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h5>Segmenti</h5>
              {p.segments.length === 0 ? (
                <p className="muted">Nessuno.</p>
              ) : (
                <ul className="segments-list">
                  {p.segments.map((s, i) => (
                    <li key={i}>
                      <code>{s.from}</code> → <code>{s.to}</code>
                      <span className="seg-type">{s.type}</span>
                      {s.viaPoints.length > 0 && (
                        <span className="muted">via {s.viaPoints.join(', ')}</span>
                      )}
                      {s.notes && <span className="muted">— {s.notes}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        ))}
      </div>

      <div className="detail-block">
        <h4>
          Passi di costruzione <span className="count">({garment.steps.length})</span>
        </h4>
        {garment.steps.length === 0 ? (
          <p className="muted">Nessun passo registrato.</p>
        ) : (
          <ol className="steps-list">
            {garment.steps.map(s => (
              <li key={s.stepNumber}>
                <p>{s.italianText}</p>
                {(s.pointsAdded.length > 0 || s.segmentsAdded.length > 0) && (
                  <p className="small">
                    {s.pointsAdded.length > 0 && `Punti: ${s.pointsAdded.join(', ')}`}
                    {s.pointsAdded.length > 0 && s.segmentsAdded.length > 0 && ' · '}
                    {s.segmentsAdded.length > 0 && `Segmenti: ${s.segmentsAdded.join(', ')}`}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {garment.additionalNotes && (
        <div className="detail-block">
          <h4>Note del libro</h4>
          <p>{garment.additionalNotes}</p>
        </div>
      )}

      <div className="row detail-actions">
        <button
          onClick={() => onCreateProject?.(garmentId)}
          disabled={!onCreateProject}
        >
          Nuovo progetto da questo capo
        </button>
      </div>
    </section>
  )
}
